import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import React, { useContext, useEffect } from "react";

import { AiInterviewerFragment } from "../../graphql";
import { AiInterviewerWizardContext } from "../AiInterviewerWizard/AiInterviewerWizardContext";
import { AiInterviewerCandidates } from "./AiInterviewerCandidates";
import { AiInterviewerTestInterviews } from "./AiInterviewerTestInterviews";
import useInterviewerTabs, { TAB_INDICES } from "./useInterviewerTabs";

interface AiInterviewerMobileContentProps {
  aiInterviewer: AiInterviewerFragment;
}

const TAB_PADDING_BOTTOM = "2.5";
const TAB_MARGIN_BOTTOM = "0";

const StyledTab: typeof Tab = (props) => (
  <Tab pb={TAB_PADDING_BOTTOM} mb={TAB_MARGIN_BOTTOM} {...props} />
);

// Mobile tab indices (different from desktop)
const MOBILE_TAB_INDICES = {
  CANDIDATES: 0,
  TEST_INTERVIEW: 1,
} as const;

// Map mobile tab indices to desktop tab indices
const MOBILE_TO_DESKTOP_TAB_MAP: Record<number, number> = {
  [MOBILE_TAB_INDICES.TEST_INTERVIEW]: TAB_INDICES.TEST_INTERVIEW,
};

const DESKTOP_TO_MOBILE_TAB_MAP: Record<number, number> = {
  [TAB_INDICES.TEST_INTERVIEW]: MOBILE_TAB_INDICES.TEST_INTERVIEW,
};

const AiInterviewerMobileContent: React.FC<AiInterviewerMobileContentProps> = ({
  aiInterviewer,
}) => {
  const {
    activeTab: desktopActiveTab,
    handleChangeTab: handleDesktopChangeTab,
  } = useInterviewerTabs({
    aiInterviewerId: aiInterviewer.id,
  });

  // Convert desktop tab index to mobile tab index
  // If on a non-mobile tab (1, 2, 3), default to Candidates (mobile 0)
  const mobileActiveTab =
    DESKTOP_TO_MOBILE_TAB_MAP[desktopActiveTab] ??
    MOBILE_TAB_INDICES.CANDIDATES;

  // Convert mobile tab change to desktop tab index
  const handleMobileChangeTab = (mobileIndex: number): void => {
    const desktopIndex =
      MOBILE_TO_DESKTOP_TAB_MAP[mobileIndex] ?? TAB_INDICES.CANDIDATES;
    handleDesktopChangeTab(desktopIndex);
  };

  // Register tab setter with wizard context
  const { registerSetActiveTab } = useContext(AiInterviewerWizardContext);
  useEffect(() => {
    registerSetActiveTab?.(handleDesktopChangeTab);
  }, [registerSetActiveTab, handleDesktopChangeTab]);

  // If user navigates to a desktop-only tab on mobile, redirect to Candidates
  // Desktop tabs 0-2 (Guide, Settings, Notifications) and 5 (Analytics) are not available on mobile
  useEffect(() => {
    if (
      desktopActiveTab !== TAB_INDICES.CANDIDATES &&
      desktopActiveTab !== TAB_INDICES.TEST_INTERVIEW
    ) {
      handleDesktopChangeTab(TAB_INDICES.CANDIDATES);
    }
  }, [desktopActiveTab, handleDesktopChangeTab]);

  return (
    <Tabs
      isLazy
      index={mobileActiveTab}
      onChange={handleMobileChangeTab}
      display="flex"
      flexDirection="column"
      flex="1"
      minH="0"
    >
      <TabList borderBottom="1px solid" borderColor="gray.200">
        <StyledTab>Candidates</StyledTab>
        <StyledTab>Test interviews</StyledTab>
      </TabList>

      <TabPanels
        display="flex"
        flexDirection="column"
        flex="1"
        minH="0"
        overflow="auto"
      >
        <TabPanel
          data-testid="ai-interviewer-candidates-panel"
          display="flex"
          flexDirection="column"
          flex="1"
          minH="0"
          px="0"
        >
          <AiInterviewerCandidates aiInterviewer={aiInterviewer} />
        </TabPanel>
        <TabPanel
          data-testid="ai-interviewer-test-interview-panel"
          display="flex"
          flexDirection="column"
          flex="1"
          minH="0"
          px="0"
        >
          <AiInterviewerTestInterviews aiInterviewer={aiInterviewer} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default AiInterviewerMobileContent;
